// The 9 platform file upload slots (feed Cash Positions / Loan Number Check /
// Cashflow Reconciliation / Manual Loan Exclusions - see cash_positions.py,
// loan_number_check.py, excludable_files.py), plus "Auto Select from Folder"
// (matches each file in a chosen folder to a slot by filename, client-side -
// matchFilenameToField() below is a port of platform_files_autodetect.py's
// match_filename_to_field() - then uploads each match).
//
// Laid out as a compact status-chip grid, not a list of rows: each platform
// is a small card (2-up on typical widths) showing its slots as inline
// pills (icon + label, colored by upload status) wrapped in a single line
// or two - the upload/replace/download controls only appear in a popover
// when a pill is clicked, instead of always taking up a full row per slot.
// 9 slots that previously needed 9 stacked rows (even at one compact row
// each) now fit in ~4 short card headers + a couple of wrapped pill lines,
// which is what actually removes the scrolling - shrinking row height
// alone couldn't get there since there were still 9 rows to stack.
//
// Two independent upload groups share the same 9 slots:
// - Cash Positions (7 fields): GET .../cash-positions/upload (status)
// - Loan Number Check (2 fields): GET .../loan-number-check/upload (status)
// Uploads go browser -> S3 via jobs.js uploadFile() (presign -> PUT ->
// complete); downloads via GET .../uploads/:field/download (presigned URL).
// Each slot uploads immediately on file selection rather than staging a
// pending file - simpler
// than a second "confirm" step, and matches "choose/replace" framing in the
// spec (selecting a new file *is* the replace action).

import React, { useEffect, useRef, useState, useCallback } from 'react';
import axiosWithAuth from '../../../helpers/AxiosHelper';
import { FileUpload } from 'primereact/fileupload';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { API, apiErrorDetail, downloadUrl, uploadFile } from '../jobs';
import errorMessage from '../../../helpers/errorMessage';

const PLATFORM_ACCENT = { FINBEE: '#6366F1', VAURAUS: '#0EA5E9', SAVY: '#10B981', MOZZENO: '#F59E0B' };

const GROUPS = {
  cash_positions: {
    statusPath: (id) => `${API}/reportings/${id}/cash-positions/upload`,
  },
  loan_number_check: {
    statusPath: (id) => `${API}/reportings/${id}/loan-number-check/upload`,
  },
};

// Exact port of platform_files_autodetect.py - rule order matters.
const COMPARTMENT_NUMBER_TO_FIELD = {
  1012: 'finbee_cash_movement',
  1105: 'mozzeno_cash_movement',
  165: 'vauraus_cash_movement',
  190: 'savy_cash_movement',
};
const COMPARTMENT_RE = /compartment[_\-\s]*([\d,]+)/i;
const normalizeFilename = (filename) => filename.toLowerCase().replace(/[^a-z0-9]/g, '');

export function matchFilenameToField(filename) {
  const normalized = normalizeFilename(filename);
  if (normalized.includes('mozzeno')) return 'mozzeno_dashboard';
  if (normalized.includes('accountstatement')) return 'finbee_account_statement';
  if (normalized.includes('financedinvoice')) return 'vauraus_loan_register';
  if (normalized.includes('activeandrepaidloan')) return 'finbee_loan_register';
  if (normalized.includes('dashboard')) return 'savy_payments';
  const match = COMPARTMENT_RE.exec(filename);
  if (match) {
    const digits = match[1].replace(/[^\d]/g, '');
    if (digits) return COMPARTMENT_NUMBER_TO_FIELD[parseInt(digits, 10)] || null;
  }
  return null;
}

// Platform -> ordered slots. Exactly 9 across the 4 platforms. Order here IS
// the row order the table renders in - keep each platform's slots
// contiguous, since DataTable's rowGroupMode="subheader" requires
// same-group rows to already be adjacent in `value` (it doesn't sort).
const PLATFORM_SLOTS = [
  { platform: 'FINBEE', field: 'finbee_cash_movement', label: 'Cash Movement', group: 'cash_positions' },
  { platform: 'FINBEE', field: 'finbee_account_statement', label: 'Account Statement', group: 'cash_positions' },
  { platform: 'FINBEE', field: 'finbee_loan_register', label: 'Loan Register', group: 'loan_number_check' },
  { platform: 'VAURAUS', field: 'vauraus_cash_movement', label: 'Cash Movement', group: 'cash_positions' },
  { platform: 'VAURAUS', field: 'vauraus_loan_register', label: 'Loan Register', group: 'loan_number_check' },
  { platform: 'SAVY', field: 'savy_cash_movement', label: 'Cash Movement', group: 'cash_positions' },
  { platform: 'SAVY', field: 'savy_payments', label: 'Dashboard (Payments)', group: 'cash_positions' },
  { platform: 'MOZZENO', field: 'mozzeno_cash_movement', label: 'Cash Movement', group: 'cash_positions' },
  { platform: 'MOZZENO', field: 'mozzeno_dashboard', label: 'Dashboard (Receivables)', group: 'cash_positions' },
];

const PlatformFilesCard = ({ reportingId, setLoading, onChange, onUploaded }) => {
  const [cashPositionsStatus, setCashPositionsStatus] = useState({});
  const [loanNumberCheckStatus, setLoanNumberCheckStatus] = useState({});
  const [error, setError] = useState(null);
  const [rowErrors, setRowErrors] = useState({});
  const [uploadingField, setUploadingField] = useState(null);
  const [autoDetectResult, setAutoDetectResult] = useState(null);
  const [autoDetecting, setAutoDetecting] = useState(false);
  const folderInputRef = useRef(null);

  const fetchStatuses = useCallback(async () => {
    if (!reportingId) return;
    setLoading(true);
    try {
      const [cashRes, loanRes] = await Promise.all([
        axiosWithAuth.get(GROUPS.cash_positions.statusPath(reportingId)),
        axiosWithAuth.get(GROUPS.loan_number_check.statusPath(reportingId)),
      ]);
      setCashPositionsStatus(cashRes.data || {});
      setLoanNumberCheckStatus(loanRes.data || {});
      setError(null);
      if (onChange) onChange({ cash_positions: cashRes.data, loan_number_check: loanRes.data });
    } catch (err) {
      console.error('[PlatformFilesCard] status fetch error:', errorMessage(err));
      setError(errorMessage(err, 'Failed to load Originator file upload status.'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportingId]);

  useEffect(() => {
    fetchStatuses();
  }, [fetchStatuses]);

  const statusFor = (group, field) => (group === 'cash_positions' ? cashPositionsStatus[field] : loanNumberCheckStatus[field]);

  const rows = PLATFORM_SLOTS.map((slot) => ({ ...slot, filename: statusFor(slot.group, slot.field) }));
  const uploadedCountByPlatform = rows.reduce((acc, r) => {
    acc[r.platform] = acc[r.platform] || { uploaded: 0, total: 0 };
    acc[r.platform].total += 1;
    if (r.filename) acc[r.platform].uploaded += 1;
    return acc;
  }, {});

  const handleSelect = async (row, e) => {
    const file = e.files && e.files[0];
    const resetInput = () => e.options && e.options.clear && e.options.clear();
    if (!file || !reportingId) {
      resetInput();
      return;
    }
    setUploadingField(row.field);
    setLoading(true);
    try {
      const data = await uploadFile(reportingId, row.field, file);
      setCashPositionsStatus(data.cash_positions || {});
      setLoanNumberCheckStatus(data.loan_number_check || {});
      const groupStatus = row.group === 'cash_positions' ? data.cash_positions : data.loan_number_check;
      if (onChange) onChange({ group: row.group, status: groupStatus });
      if (onUploaded) onUploaded();
      setRowErrors((prev) => ({ ...prev, [row.field]: null }));
    } catch (err) {
      console.error(`[PlatformFilesCard] upload error (${row.field}):`, err.detail || errorMessage(err));
      setRowErrors((prev) => ({ ...prev, [row.field]: apiErrorDetail(err, 'Upload failed.') }));
    } finally {
      setUploadingField(null);
      setLoading(false);
      resetInput();
    }
  };

  const handleDownload = async (row) => {
    if (!reportingId || !row.filename) return;
    setLoading(true);
    try {
      const res = await axiosWithAuth.get(`${API}/reportings/${reportingId}/uploads/${row.field}/download`);
      downloadUrl(res.data.url, res.data.filename || row.filename);
    } catch (err) {
      console.error(`[PlatformFilesCard] download error (${row.field}):`, errorMessage(err));
      setRowErrors((prev) => ({ ...prev, [row.field]: errorMessage(err, 'Download failed.') }));
    } finally {
      setLoading(false);
    }
  };

  const handleAutoDetectClick = () => {
    if (folderInputRef.current) folderInputRef.current.click();
  };

  const handleFolderSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ''; // allow re-selecting the same folder later
    if (files.length === 0 || !reportingId) return;
    setAutoDetecting(true);
    setLoading(true);
    try {
      // Same summary the old server-side auto-detect endpoint returned:
      // first file per field wins, later ones -> duplicates, no match ->
      // unmatched; matched = only the uploads that actually succeeded.
      const picked = {};
      const duplicates = {};
      const unmatched = [];
      files.forEach((f) => {
        const field = matchFilenameToField(f.name || '');
        if (field === null) unmatched.push(f.name || '');
        else if (picked[field]) (duplicates[field] = duplicates[field] || []).push(f.name || '');
        else picked[field] = f;
      });
      const matched = {};
      const rejected = {};
      let latest = null;
      for (const [field, f] of Object.entries(picked)) {
        try {
          latest = await uploadFile(reportingId, field, f, { autoDetect: true });
          matched[field] = f.name || '';
        } catch (err) {
          console.error(`[PlatformFilesCard] auto-detect upload error (${field}):`, err.detail || errorMessage(err));
          rejected[field] = `${f.name}: ${apiErrorDetail(err, 'Upload failed')}`;
        }
      }
      const result = { matched, unmatched, duplicates, rejected };
      if (latest) {
        result.cash_positions = latest.cash_positions;
        result.loan_number_check = latest.loan_number_check;
        setCashPositionsStatus(latest.cash_positions || {});
        setLoanNumberCheckStatus(latest.loan_number_check || {});
      }
      setAutoDetectResult(result);
      setError(null);
      if (onChange) onChange(result);
      if (latest && onUploaded) onUploaded();
    } catch (err) {
      console.error('[PlatformFilesCard] auto-detect error:', errorMessage(err));
      setError(errorMessage(err, 'Auto Select from Folder failed.'));
    } finally {
      setAutoDetecting(false);
      setLoading(false);
    }
  };

  // Status (check/x + label) and the upload action are now visually split,
  // not one icon doing double duty - a checkmark read as "done", not as
  // "click me", so there was no visible affordance for where to click.
  // Left: a plain, non-interactive status pill. Right: a distinct upload
  // icon-button (always the same "pi-upload" glyph, never changes with
  // status) that's unambiguously the click target, plus a download button
  // once a file exists.
  const FileChip = ({ row }) => (
    <span className={`cr-file-chip${row.filename ? ' is-uploaded' : ''}`}>
      <i className={`pi ${row.filename ? 'pi-check-circle' : 'pi-times-circle'} cr-file-chip-icon`} />
      <span className="cr-file-chip-label">{row.label}</span>

      <FileUpload
        mode="basic"
        name={row.field}
        chooseOptions={{
          icon: 'pi pi-upload',
          iconOnly: true,
          className: 'p-button-sm p-button-rounded p-button-text',
        }}
        auto
        customUpload
        uploadHandler={(e) => handleSelect(row, e)}
        accept=".xlsx,.xlsm,.csv"
        disabled={uploadingField === row.field}
        tooltip={row.filename ? 'Replace' : 'Upload'}
        tooltipOptions={{ position: 'top' }}
      />
      {row.filename && (
        <Button
          icon="pi pi-download"
          className="p-button-rounded p-button-text p-button-sm"
          onClick={() => handleDownload(row)}
          tooltip={`Download ${row.filename}`}
          tooltipOptions={{ position: 'top' }}
        />
      )}
      {rowErrors[row.field] && (
        <i className="pi pi-exclamation-triangle cr-file-chip-error" title={rowErrors[row.field]} />
      )}
    </span>
  );

  return (
    <div>
      <div className="ex-section-header">
        <h4 className="ex-section-title">Originator Files</h4>
        <div>
          <input
            ref={folderInputRef}
            type="file"
            webkitdirectory="true"
            directory=""
            multiple
            style={{ display: 'none' }}
            onChange={handleFolderSelected}
          />
          <Button
            label="Auto Select from Folder"
            icon="pi pi-folder-open"
            className="p-button-outlined p-button-sm"
            onClick={handleAutoDetectClick}
            disabled={!reportingId || autoDetecting}
            loading={autoDetecting}
          />
        </div>
      </div>

      {error && <div className="ex-inline-error mb-3">{error}</div>}

      {autoDetectResult && (
        <div className="cr-note-box mb-3">
          <div>
            <strong>Matched:</strong>{' '}
            {Object.keys(autoDetectResult.matched || {}).length === 0
              ? 'none'
              : Object.entries(autoDetectResult.matched).map(([field, fname]) => `${field} <- ${fname}`).join(', ')}
          </div>
          {autoDetectResult.unmatched && autoDetectResult.unmatched.length > 0 && (
            <div>
              <strong>Unmatched:</strong> {autoDetectResult.unmatched.join(', ')}
            </div>
          )}
          {autoDetectResult.duplicates && Object.keys(autoDetectResult.duplicates).length > 0 && (
            <div className="cr-warning-text">
              <strong>Duplicates (only first used):</strong>{' '}
              {Object.entries(autoDetectResult.duplicates).map(([field, names]) => `${field}: ${names.join(', ')}`).join('; ')}
            </div>
          )}
          {autoDetectResult.rejected && Object.keys(autoDetectResult.rejected).length > 0 && (
            <div className="cr-danger-text">
              <strong>Rejected:</strong>{' '}
              {Object.entries(autoDetectResult.rejected).map(([field, reason]) => `${field}: ${reason}`).join('; ')}
            </div>
          )}
        </div>
      )}

      <div className="cr-platform-grid">
        {Object.keys(uploadedCountByPlatform).map((platform) => {
          const platformRows = rows.filter((r) => r.platform === platform);
          const counts = uploadedCountByPlatform[platform];
          const accent = PLATFORM_ACCENT[platform] || '#6366F1';
          return (
            <div key={platform} className="cr-platform-card">
              <div className="cr-platform-card-header">
                <span className="cr-platform-dot" style={{ background: accent }} />
                <strong className="cr-platform-name">{platform}</strong>
                <Tag
                  severity={counts.uploaded === counts.total ? 'success' : 'secondary'}
                  className={counts.uploaded === counts.total ? undefined : 'cr-tag-muted'}
                  value={`${counts.uploaded}/${counts.total}`}
                />
              </div>
              <div className="flex flex-wrap gap-2">
                {platformRows.map((row) => (
                  <FileChip key={row.field} row={row} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PlatformFilesCard;
